import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Package, ArrowLeft, User, MapPin, RefreshCw, ChevronDown, ChevronRight } from 'lucide-react';
import AdminNavigation from '../../components/ui/AdminNavigation';
import { abidjanDistributionService } from '../../services/abidjanDistributionService';
import { getStatusLabel } from '../../services/containerService';

/**
 * Abidjan Container Detail Management
 * Manages individual container delivery operations with task assignment and agent coordination
 */
export default function AbidjanContainerDetailManagement() {
  const { containerId } = useParams();
  const navigate = useNavigate();
  const [containerData, setContainerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [agents, setAgents] = useState([]);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [filterZone, setFilterZone] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCommune, setFilterCommune] = useState('');
  const [filterQuartier, setFilterQuartier] = useState('');
  
  // Accordion state for commune/quartier grouping
  const [expandedCommunes, setExpandedCommunes] = useState({});
  const [expandedQuartiers, setExpandedQuartiers] = useState({});

  useEffect(() => {
    if (containerId) {
      fetchContainerDetail();
      fetchAgents();
    }
  }, [containerId]);

  const fetchContainerDetail = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await abidjanDistributionService?.getContainerDetail(containerId);
      setContainerData(data);
      
      // Auto-expand all communes and quartiers
      if (data?.grouped) {
        const communes = {};
        const quartiers = {};
        Object?.keys(data?.grouped)?.forEach(commune => {
          communes[commune] = true;
          quartiers[commune] = {};
          Object?.keys(data?.grouped?.[commune])?.forEach(quartier => {
            quartiers[commune][quartier] = true;
          });
        });
        setExpandedCommunes(communes);
        setExpandedQuartiers(quartiers);
      }
    } catch (err) {
      setError(err?.message || 'Erreur lors du chargement du conteneur');
      console.error('Error fetching container detail:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAgents = async () => {
    try {
      const agentsData = await abidjanDistributionService?.getAbidjanAgents();
      setAgents(agentsData);
    } catch (err) {
      console.error('Error fetching agents:', err);
    }
  };

  // Handle reassignment
  const handleReassign = async () => {
    if (!selectedTask || !selectedAgent) return;

    try {
      setActionLoading(true);
      await abidjanDistributionService?.reassignDeliveryTask(selectedTask?.id, selectedAgent);
      setShowReassignModal(false);
      setSelectedTask(null);
      setSelectedAgent('');
      await fetchContainerDetail();
      alert('Agent réassigné avec succès');
    } catch (err) {
      alert(err?.message || 'Erreur lors de la réassignation');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle resolve incident
  const handleResolveIncident = async (taskId) => {
    if (!confirm('Voulez-vous vraiment repasser cette livraison à A_PREPARER ?')) return;

    try {
      setActionLoading(true);
      await abidjanDistributionService?.resolveIncident(taskId);
      await fetchContainerDetail();
      alert('Incident résolu avec succès');
    } catch (err) {
      alert(err?.message || 'Erreur lors de la résolution de l\'incident');
    } finally {
      setActionLoading(false);
    }
  };

  // Get status badge styling
  const getStatusBadge = (status) => {
    switch (status) {
      case 'A_PREPARER':
        return 'bg-yellow-100 text-yellow-800';
      case 'EN_COURS':
        return 'bg-blue-100 text-blue-800';
      case 'LIVRE':
        return 'bg-green-100 text-green-800';
      case 'INCIDENT':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Filter delivery tasks with commune/quartier
  const filteredTasks = containerData?.deliveryTasks?.filter(task => {
    const matchesZone = !filterZone || task?.deliveryZone === filterZone;
    const matchesStatus = !filterStatus || task?.status === filterStatus;
    const matchesCommune = !filterCommune || task?.deliveryCommune === filterCommune;
    const matchesQuartier = !filterQuartier || task?.deliveryQuartier === filterQuartier;
    return matchesZone && matchesStatus && matchesCommune && matchesQuartier;
  }) || [];

  // Get unique communes and quartiers for filters
  const communes = [...new Set(containerData?.deliveryTasks?.map(t => t?.deliveryCommune)?.filter(Boolean))];
  const quartiers = [...new Set(
    containerData?.deliveryTasks
      ?.filter(t => !filterCommune || t?.deliveryCommune === filterCommune)
      ?.map(t => t?.deliveryQuartier)
      ?.filter(Boolean)
  )];

  // Toggle commune expansion
  const toggleCommune = (commune) => {
    setExpandedCommunes(prev => ({
      ...prev,
      [commune]: !prev?.[commune]
    }));
  };

  // Toggle quartier expansion
  const toggleQuartier = (commune, quartier) => {
    setExpandedQuartiers(prev => ({
      ...prev,
      [commune]: {
        ...(prev?.[commune] || {}),
        [quartier]: !prev?.[commune]?.[quartier]
      }
    }));
  };

  // Update the getStatusColor function
  const getStatusColor = (status) => {
    const colors = {
      'AU_PORT_LE_HAVRE_ANVERS': 'bg-gray-100 text-gray-800',
      'EN_TRANSIT_MARITIME': 'bg-blue-100 text-blue-800',
      'ARRIVE_A_ABIDJAN': 'bg-purple-100 text-purple-800',
      'DEDOUANEMENT_EN_COURS': 'bg-yellow-100 text-yellow-800',
      'TRANSFERT_A_ENTREPOT': 'bg-orange-100 text-orange-800',
      'DISTRIBUTION_EN_COURS': 'bg-indigo-100 text-indigo-800',
      'CONTENEUR_TERMINE': 'bg-green-100 text-green-800'
    };
    return colors?.[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AdminNavigation />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Chargement du conteneur...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !containerData) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AdminNavigation />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
            <p className="font-medium">Erreur</p>
            <p className="text-sm">{error || 'Conteneur introuvable'}</p>
          </div>
        </div>
      </div>
    );
  }

  const { container, deliveryTasks, grouped } = containerData || {};

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNavigation />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate('/abidjan-container-distribution-dashboard')}
          className="mb-6 flex items-center text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Retour aux conteneurs
        </button>

        {/* Container Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <Package className="w-8 h-8 text-green-600 mr-3" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {container?.containerCode}
                </h1>
                <p className="text-gray-600">{container?.vesselName}</p>
              </div>
            </div>
            <button
              onClick={fetchContainerDetail}
              disabled={actionLoading}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>

          {/* Container Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="text-sm text-blue-600 mb-1">Total Livraisons</div>
              <div className="text-2xl font-bold text-blue-900">{container?.totalDeliveries}</div>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <div className="text-sm text-green-600 mb-1">Livrées</div>
              <div className="text-2xl font-bold text-green-900">{container?.deliveredCount}</div>
            </div>
            <div className="bg-red-50 rounded-lg p-4">
              <div className="text-sm text-red-600 mb-1">Incidents</div>
              <div className="text-2xl font-bold text-red-900">{container?.incidentCount}</div>
            </div>
          </div>
        </div>

        {/* Container Status */}
        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
          <h3 className="text-lg font-semibold mb-4">Statut du conteneur</h3>
          <div className="space-y-3">
            <div>
              <p className="text-sm text-gray-500">Statut actuel</p>
              <div className="mt-1 px-4 py-3 bg-gray-50 rounded-lg border border-gray-200">
                <p className="font-semibold text-gray-900">
                  {getStatusLabel(container?.status) || 'Statut inconnu'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters - UPDATED with Commune/Quartier */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Commune
              </label>
              <select
                value={filterCommune}
                onChange={(e) => {
                  setFilterCommune(e?.target?.value);
                  setFilterQuartier(''); // Reset quartier when commune changes
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="">Toutes les communes</option>
                {communes?.sort()?.map(commune => (
                  <option key={commune} value={commune}>{commune}</option>
                ))}
              </select>
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Quartier
              </label>
              <select
                value={filterQuartier}
                onChange={(e) => setFilterQuartier(e?.target?.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                disabled={!filterCommune}
              >
                <option value="">Tous les quartiers</option>
                {quartiers?.sort()?.map(quartier => (
                  <option key={quartier} value={quartier}>{quartier}</option>
                ))}
              </select>
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Zone
              </label>
              <select
                value={filterZone}
                onChange={(e) => setFilterZone(e?.target?.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="">Toutes les zones</option>
                {[...new Set(containerData?.deliveryTasks?.map(t => t?.deliveryZone)?.filter(Boolean))]?.map(zone => (
                  <option key={zone} value={zone}>{zone}</option>
                ))}
              </select>
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Statut
              </label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e?.target?.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="">Tous les statuts</option>
                <option value="A_PREPARER">À Préparer</option>
                <option value="EN_COURS">En Cours</option>
                <option value="LIVRE">Livré</option>
                <option value="INCIDENT">Incident</option>
              </select>
            </div>
          </div>
        </div>

        {/* Grouped View - Accordion Style (Commune → Quartier → Tasks) */}
        {grouped && Object?.keys(grouped)?.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-6">
            <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Vue par Commune / Quartier
              </h3>
            </div>
            
            <div className="divide-y divide-gray-200">
              {Object?.keys(grouped)?.sort()?.map((commune) => {
                const isExpandedCommune = expandedCommunes?.[commune];
                const communeQuartiers = grouped?.[commune] || {};

                return (
                  <div key={commune} className="bg-white">
                    {/* Commune Header */}
                    <button
                      onClick={() => toggleCommune(commune)}
                      className="w-full bg-blue-50 hover:bg-blue-100 p-4 flex items-center justify-between transition-colors"
                    >
                      <div className="flex items-center space-x-3">
                        {isExpandedCommune ? (
                          <ChevronDown className="w-5 h-5 text-blue-600" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-blue-600" />
                        )}
                        <MapPin className="w-5 h-5 text-blue-600" />
                        <span className="font-bold text-blue-900">{commune}</span>
                      </div>
                    </button>

                    {/* Quartiers Level */}
                    {isExpandedCommune && (
                      <div className="bg-white pl-8">
                        {Object?.keys(communeQuartiers)?.sort()?.map((quartier) => {
                          const isExpandedQuartier = expandedQuartiers?.[commune]?.[quartier];
                          const quartierData = communeQuartiers?.[quartier];
                          const counters = quartierData?.counters;

                          return (
                            <div key={quartier} className="border-t border-gray-100">
                              {/* Quartier Header with Counters */}
                              <button
                                onClick={() => toggleQuartier(commune, quartier)}
                                className="w-full bg-gray-50 hover:bg-gray-100 p-3 flex items-center justify-between transition-colors"
                              >
                                <div className="flex items-center space-x-3">
                                  {isExpandedQuartier ? (
                                    <ChevronDown className="w-4 h-4 text-gray-600" />
                                  ) : (
                                    <ChevronRight className="w-4 h-4 text-gray-600" />
                                  )}
                                  <span className="font-semibold text-gray-900">{quartier}</span>
                                </div>
                                
                                {/* Counters */}
                                <div className="flex items-center space-x-2 text-xs">
                                  <span className="px-2 py-1 bg-gray-200 text-gray-800 rounded font-medium">
                                    Total: {counters?.total}
                                  </span>
                                  <span className="px-2 py-1 bg-green-100 text-green-800 rounded font-medium">
                                    Livré: {counters?.LIVRE}
                                  </span>
                                  <span className="px-2 py-1 bg-red-100 text-red-800 rounded font-medium">
                                    Incident: {counters?.INCIDENT}
                                  </span>
                                  <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded font-medium">
                                    Restantes: {counters?.remaining}
                                  </span>
                                </div>
                              </button>

                              {/* Tasks List */}
                              {isExpandedQuartier && (
                                <div className="bg-white divide-y divide-gray-50">
                                  {quartierData?.tasks?.map((task) => (
                                    <div key={task?.id} className="p-4 hover:bg-gray-50 flex items-start justify-between">
                                      <div className="flex-1">
                                        <div className="flex items-center space-x-2 mb-1">
                                          <span className="font-medium text-gray-900">{task?.recipientName}</span>
                                          <span className={`px-2 py-0.5 text-xs rounded-full ${getStatusBadge(task?.status)}`}>
                                            {task?.status}
                                          </span>
                                        </div>
                                        <p className="text-sm text-gray-600">{task?.recipientPhone}</p>
                                        <p className="text-sm text-gray-500 mt-1">{task?.deliveryAddress}</p>
                                        {task?.agent && (
                                          <p className="text-xs text-gray-500 mt-1">
                                            Agent: {task?.agent?.name}
                                          </p>
                                        )}
                                      </div>
                                      
                                      <div className="flex items-center gap-2 ml-4">
                                        {task?.status === 'INCIDENT' && (
                                          <button
                                            onClick={() => handleResolveIncident(task?.id)}
                                            disabled={actionLoading}
                                            className="text-xs px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                                          >
                                            Résoudre
                                          </button>
                                        )}
                                        <button
                                          onClick={() => {
                                            setSelectedTask(task);
                                            setSelectedAgent(task?.agent?.id || '');
                                            setShowReassignModal(true);
                                          }}
                                          disabled={actionLoading}
                                          className="text-xs px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                                        >
                                          Réassigner
                                        </button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Delivery Tasks Table - Desktop */}
        <div className="hidden md:block bg-white rounded-lg shadow-sm overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Adresse</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Zone</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Agent</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredTasks?.map((task) => (
                <tr key={task?.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">{task?.recipientName}</div>
                    <div className="text-sm text-gray-500">{task?.recipientPhone}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900 max-w-xs truncate">{task?.deliveryAddress}</div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded">
                      {task?.deliveryZone || 'N/A'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{task?.agent?.name || 'Non assigné'}</div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadge(task?.status)}`}>
                      {task?.status}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      {task?.status === 'INCIDENT' && (
                        <button
                          onClick={() => handleResolveIncident(task?.id)}
                          disabled={actionLoading}
                          className="text-xs px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                        >
                          Résoudre
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setSelectedTask(task);
                          setSelectedAgent(task?.agent?.id || '');
                          setShowReassignModal(true);
                        }}
                        disabled={actionLoading}
                        className="text-xs px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                      >
                        Réassigner
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Delivery Tasks Cards - Mobile */}
        <div className="md:hidden space-y-4">
          {filteredTasks?.map((task) => (
            <div key={task?.id} className="bg-white rounded-lg shadow-sm p-4">
              <div className="flex items-center justify-between mb-3">
                <span className={`px-3 py-1 text-xs leading-5 font-semibold rounded-full ${getStatusBadge(task?.status)}`}>
                  {task?.status}
                </span>
                {task?.deliveryZone && (
                  <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded">
                    {task?.deliveryZone}
                  </span>
                )}
              </div>

              <div className="space-y-2 mb-3">
                <div className="flex items-start">
                  <User className="w-4 h-4 text-gray-400 mr-2 mt-1" />
                  <div>
                    <div className="text-sm font-medium text-gray-900">{task?.recipientName}</div>
                    <div className="text-xs text-gray-500">{task?.recipientPhone}</div>
                  </div>
                </div>

                <div className="flex items-start">
                  <MapPin className="w-4 h-4 text-gray-400 mr-2 mt-1" />
                  <div className="text-sm text-gray-600">{task?.deliveryAddress}</div>
                </div>

                <div className="flex items-start">
                  <User className="w-4 h-4 text-gray-400 mr-2 mt-1" />
                  <div className="text-sm text-gray-600">
                    Agent: {task?.agent?.name || 'Non assigné'}
                  </div>
                </div>
              </div>

              <div className="flex gap-2">
                {task?.status === 'INCIDENT' && (
                  <button
                    onClick={() => handleResolveIncident(task?.id)}
                    disabled={actionLoading}
                    className="flex-1 text-xs px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                  >
                    Résoudre
                  </button>
                )}
                <button
                  onClick={() => {
                    setSelectedTask(task);
                    setSelectedAgent(task?.agent?.id || '');
                    setShowReassignModal(true);
                  }}
                  disabled={actionLoading}
                  className="flex-1 text-xs px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  Réassigner
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {filteredTasks?.length === 0 && (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Aucune livraison trouvée
            </h3>
            <p className="text-gray-600">
              {filterZone || filterStatus || filterCommune || filterQuartier ? 
                'Essayez de modifier vos filtres' : 
                'Ce conteneur ne contient aucune livraison'}
            </p>
          </div>
        )}
      </div>

      {/* Reassign Modal */}
      {showReassignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              Réassigner Agent
            </h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Client: {selectedTask?.recipientName}
              </label>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sélectionner un nouvel agent
              </label>
              <select
                value={selectedAgent}
                onChange={(e) => setSelectedAgent(e?.target?.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="">Choisir un agent...</option>
                {agents?.map(agent => (
                  <option key={agent?.id} value={agent?.id}>
                    {agent?.name} {agent?.zone ? `(${agent?.zone})` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowReassignModal(false);
                  setSelectedTask(null);
                  setSelectedAgent('');
                }}
                disabled={actionLoading}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                onClick={handleReassign}
                disabled={actionLoading || !selectedAgent}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                {actionLoading ? 'En cours...' : 'Confirmer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}